import { useEffect, useMemo, useState } from 'react';
import { Save, RefreshCcw } from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import ErrorBanner from '../components/ErrorBanner.jsx';
import { pricingAPI } from '../services/api.js';
import { DEFAULTS, computeCosts } from '../lib/pricingMath.js';

// INTERNAL pricing / cost calculator (owner-only). Loads the editable rates +
// assumptions + markup, lets the owner edit every one, and LIVE-COMPUTES (in
// the browser, via pricingMath.js which mirrors the server) the per-minute cost
// breakdown, total $/min cost, and suggested price packages with margin.

// Field groups define the editable form. Each field is one rate/assumption.
const VARIABLE_FIELDS = [
  { key: 'deepgramSttPerMin', label: 'Deepgram STT (nova-3)', suffix: '$/min', step: '0.0001' },
  { key: 'auraTtsPer1kChars', label: 'Deepgram Aura-2 TTS', suffix: '$/1k chars', step: '0.001' },
  { key: 'elevenLabsPer1kChars', label: 'ElevenLabs Flash TTS', suffix: '$/1k chars', step: '0.001' },
  { key: 'claudeInputPerMtok', label: 'Claude input', suffix: '$/1M tokens', step: '0.01' },
  { key: 'claudeOutputPerMtok', label: 'Claude output', suffix: '$/1M tokens', step: '0.01' },
  { key: 'livekitPerMin', label: 'LiveKit (self-hosted)', suffix: '$/min', step: '0.0001' }
];

const ASSUMPTION_FIELDS = [
  { key: 'charsPerMinute', label: 'Speaking rate', suffix: 'chars/min', step: '10' },
  { key: 'aiTalkTimePercent', label: 'AI talk-time', suffix: '% of call', step: '5' },
  { key: 'claudeInputTokensPerMin', label: 'Claude input tokens', suffix: 'tokens/min', step: '50' },
  { key: 'claudeOutputTokensPerMin', label: 'Claude output tokens', suffix: 'tokens/min', step: '50' }
];

const FIXED_FIELDS = [
  { key: 'hetznerMonthly', label: 'Hetzner VPS (the box)', suffix: '$/mo', step: '1' },
  { key: 'railwayMonthly', label: 'Railway (core)', suffix: '$/mo', step: '1' },
  { key: 'netlifyMonthly', label: 'Netlify (dashboard)', suffix: '$/mo', step: '1' },
  { key: 'assumedMonthlyMinutes', label: 'Assumed monthly call-minutes', suffix: 'min/mo', step: '100' }
];

const NUMERIC_KEYS = [
  ...VARIABLE_FIELDS, ...ASSUMPTION_FIELDS, ...FIXED_FIELDS, { key: 'markupPercent' }
].map((f) => f.key);

const usd = (n, dp = 4) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
const usd2 = (n) => usd(n, 2);
const pct = (n) => `${Number(n || 0).toFixed(1)}%`;

export default function Pricing() {
  const [config, setConfig] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await pricingAPI.get();
      setConfig({ ...DEFAULTS, ...(r?.data || {}) });
    } catch (err) {
      setError(err.message || 'Failed to load pricing config.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Live recompute in the browser as the owner edits — no save needed to see it.
  const computed = useMemo(() => computeCosts(config), [config]);

  const setField = (key, value, numeric = true) => {
    setConfig((c) => ({ ...c, [key]: numeric ? Number(value) : value }));
    setSavedAt(null);
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {};
      for (const k of NUMERIC_KEYS) payload[k] = Number(config[k]);
      payload.ttsVendor = config.ttsVendor;
      const r = await pricingAPI.update(payload);
      setConfig({ ...DEFAULTS, ...(r?.data || {}) });
      setSavedAt(new Date());
    } catch (err) {
      setError(err.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const renderField = (f) => (
    <div key={f.key}>
      <label className="block text-sm text-stone-300 mb-1">
        {f.label} <span className="text-stone-500">({f.suffix})</span>
      </label>
      <input
        type="number"
        step={f.step}
        min="0"
        className="field-input"
        value={config[f.key] ?? ''}
        onChange={(e) => setField(f.key, e.target.value)}
        disabled={loading || saving}
      />
    </div>
  );

  return (
    <div className="px-6 md:px-10 py-8 max-w-5xl">
      <PageHeader
        title="Pricing & Costs"
        subtitle="Internal: true per-minute cost of an AI call, and price packages from it"
        actions={
          <button type="button" onClick={load} disabled={loading} className="btn-ghost">
            <RefreshCcw size={14} />
            Refresh
          </button>
        }
      />

      {error && (
        <div className="mb-6">
          <ErrorBanner message={error} />
        </div>
      )}

      {/* Headline cost + price */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="card">
          <div className="text-xs uppercase tracking-wider text-stone-500">Cost / minute</div>
          <div className="text-3xl text-stone-100 mt-1">{usd(computed.costPerMin)}</div>
          <div className="text-xs text-stone-500 mt-1">everything compounded</div>
        </div>
        <div className="card">
          <div className="text-xs uppercase tracking-wider text-stone-500">Suggested price / minute</div>
          <div className="text-3xl text-alfred-gold mt-1">{usd(computed.pricePerMin)}</div>
          <div className="text-xs text-stone-500 mt-1">
            {Number(config.markupPercent || 0).toFixed(0)}% markup (×{computed.markupMultiplier.toFixed(2)})
          </div>
        </div>
        <div className="card">
          <div className="text-xs uppercase tracking-wider text-stone-500">Margin / minute</div>
          <div className="text-3xl text-stone-100 mt-1">{usd(computed.marginPerMin)}</div>
          <div className="text-xs text-stone-500 mt-1">{pct(computed.marginPercent)} margin</div>
        </div>
      </div>

      {/* Cost breakdown — each component's contribution to $/min */}
      <section className="card mb-8">
        <h2 className="text-xl text-stone-100 mb-1">Per-minute cost breakdown</h2>
        <p className="text-sm text-stone-400 mb-4">
          cost/min = sum(variable per-min) + sum(fixed monthly) ÷ assumed monthly minutes
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-stone-500 border-b border-stone-800">
                <th className="py-2 pr-4">Component</th>
                <th className="py-2 pr-4">Type</th>
                <th className="py-2 pr-4 text-right">$ / min</th>
                <th className="py-2 text-right">% of cost</th>
              </tr>
            </thead>
            <tbody>
              {computed.breakdown.map((b) => (
                <tr key={b.key} className="border-b border-stone-900">
                  <td className="py-2 pr-4 text-stone-200">{b.label}</td>
                  <td className="py-2 pr-4 text-stone-500 capitalize">{b.kind}</td>
                  <td className="py-2 pr-4 text-right text-stone-200">{usd(b.perMin, 5)}</td>
                  <td className="py-2 text-right text-stone-500">
                    {computed.costPerMin > 0 ? pct((b.perMin / computed.costPerMin) * 100) : '—'}
                  </td>
                </tr>
              ))}
              <tr className="font-medium">
                <td className="py-2 pr-4 text-stone-100">Total cost / minute</td>
                <td className="py-2 pr-4" />
                <td className="py-2 pr-4 text-right text-alfred-gold">{usd(computed.costPerMin)}</td>
                <td className="py-2 text-right text-stone-400">100%</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-xs text-stone-500 mt-3">
          Fixed overhead is {usd2(computed.fixedMonthly)}/mo amortized over{' '}
          {Number(config.assumedMonthlyMinutes || 0).toLocaleString()} min ={' '}
          {usd(computed.fixedPerMin, 5)}/min. Variable total: {usd(computed.variablePerMin, 5)}/min.
        </p>
      </section>

      {/* Suggested price packages */}
      <section className="card mb-8">
        <h2 className="text-xl text-stone-100 mb-1">Suggested price packages</h2>
        <p className="text-sm text-stone-400 mb-4">
          Monthly bundles at the suggested per-minute price, with the resulting margin.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-stone-500 border-b border-stone-800">
                <th className="py-2 pr-4">Bundle</th>
                <th className="py-2 pr-4 text-right">Price</th>
                <th className="py-2 pr-4 text-right">Our cost</th>
                <th className="py-2 pr-4 text-right">Margin</th>
                <th className="py-2 text-right">Margin %</th>
              </tr>
            </thead>
            <tbody>
              {computed.packages.map((p) => (
                <tr key={p.minutes} className="border-b border-stone-900">
                  <td className="py-2 pr-4 text-stone-200">{p.minutes.toLocaleString()} min</td>
                  <td className="py-2 pr-4 text-right text-alfred-gold">{usd2(p.price)}</td>
                  <td className="py-2 pr-4 text-right text-stone-400">{usd2(p.cost)}</td>
                  <td className="py-2 pr-4 text-right text-stone-200">{usd2(p.margin)}</td>
                  <td className="py-2 text-right text-stone-500">{pct(p.marginPercent)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Editable rates */}
      <section className="card mb-8">
        <h2 className="text-xl text-stone-100 mb-1">Rates & assumptions</h2>
        <p className="text-sm text-stone-400 mb-6">
          Defaults are early-2026 vendor rates. Edit any value — the numbers above
          recompute instantly. Save to persist.
        </p>

        <h3 className="text-sm uppercase tracking-wider text-stone-500 mb-3">
          Variable (scale with call minutes)
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm text-stone-300 mb-1">TTS vendor to count</label>
            <select
              className="field-input"
              value={config.ttsVendor || 'elevenlabs'}
              onChange={(e) => setField('ttsVendor', e.target.value, false)}
              disabled={loading || saving}
            >
              <option value="elevenlabs">ElevenLabs Flash</option>
              <option value="aura">Deepgram Aura-2</option>
            </select>
          </div>
          {VARIABLE_FIELDS.map(renderField)}
        </div>

        <h3 className="text-sm uppercase tracking-wider text-stone-500 mb-3">Assumptions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          {ASSUMPTION_FIELDS.map(renderField)}
        </div>

        <h3 className="text-sm uppercase tracking-wider text-stone-500 mb-3">
          Fixed monthly overhead
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          {FIXED_FIELDS.map(renderField)}
        </div>

        <h3 className="text-sm uppercase tracking-wider text-stone-500 mb-3">Markup</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm text-stone-300 mb-1">
              Markup <span className="text-stone-500">(%)</span>
            </label>
            <input
              type="number"
              step="5"
              min="0"
              className="field-input"
              value={config.markupPercent ?? ''}
              onChange={(e) => setField('markupPercent', e.target.value)}
              disabled={loading || saving}
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button type="button" className="btn-primary" onClick={save} disabled={saving || loading}>
            <Save size={16} />
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          {savedAt && <span className="text-xs text-stone-500">Saved {savedAt.toLocaleTimeString()}</span>}
        </div>
      </section>
    </div>
  );
}
